import { EditOutlined } from "@ant-design/icons";
import { DatePicker, Form, message, Modal, Tooltip, Typography } from "antd";
import { showConfirmModal } from "components/modals/confirmModal";
import { modalSubtitleStyle } from "components/modals/ModalChrome";
import { AffectedBookingList } from "components/modals/WindowConflictModal.component";
import { LaterBooking } from "shared/types";
import { useForm } from "antd/es/form/Form";
import { Button } from "antd/lib";
import { useUpdateCustomerPackage } from "hooks/mutations/customerpackage";
import { useState } from "react";
import { translate, translateWithVars } from "shared/languages";
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATE_SUBMIT_FORMAT,
  formatDate,
  now,
  tzDate,
} from "shared/utils/dayjs";

interface UpdateCustomerPackageModalProps {
  packageId: string;
  disabled?: boolean;
  /**
   * Pembatasan Admin: disabled with NO popover at all. A Manager's
   * restriction is deliberately silent on hover (Andrew, 2026-09-03).
   */
  restricted?: boolean;
  /**
   * An unactivated first-use package: the hover copy explains that a hand-set
   * date counts as activation (the server locks the row on this edit).
   */
  firstUse?: boolean;
  /** Hover text while disabled; defaults to the no-credits reason. */
  disabledReason?: string;
  /**
   * The latest class the package still has to pay for. A day before it
   * cancels the bookings after the new date, which the admin confirms first
   * (System Review C8, Andrew 2026-09-28).
   */
  lastBookedClassAt?: string | null;
}

const UpdateCustomerPackageModal = ({
  packageId,
  disabled = false,
  restricted = false,
  firstUse = false,
  disabledReason,
  lastBookedClassAt,
}: UpdateCustomerPackageModalProps) => {
  const lastClassDay = lastBookedClassAt ? tzDate(lastBookedClassAt).startOf("day") : null;
  const earliestDay = now().startOf("day");
  const [form] = useForm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  // An end date before booked classes: the server lists them, and the admin
  // confirms cancelling them in the same save (their credits come back).
  const confirmCancelLater = (
    bookings: LaterBooking[],
    retry: () => void,
    expiryDate: string,
  ) =>
    showConfirmModal({
      tone: "error",
      destructive: true,
      width: 460,
      title: translate("expiry_cancel.title"),
      content: (
        <>
          <Typography.Text style={modalSubtitleStyle}>
            {translateWithVars("expiry_cancel.body", {
              date: formatDate(tzDate(expiryDate).toDate()),
            })}
          </Typography.Text>
          <AffectedBookingList bookings={bookings} style={{ marginTop: 12 }} />
        </>
      ),
      confirmLabel: translate("expiry_cancel.confirm"),
      onConfirm: retry,
    });
  const { mutate: updateCustomerPackage } = useUpdateCustomerPackage(
    (bookings, form) =>
      confirmCancelLater(
        bookings,
        () => updateCustomerPackage({ ...form, cancel_later_bookings: true }),
        form.expiry_date!,
      ),
  );

  const onSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        updateCustomerPackage({
          packageId,
          // expiry_date from DatePicker is already dayjs from antd
          expiry_date: values.expiry_date.format(DEFAULT_DATE_SUBMIT_FORMAT),
        });
        setIsModalOpen(false);
      })
      .catch(() => {
        message.error(translate("messages.error.invalid_form"));
        setIsModalOpen(false);
      });
  };

  return (
    <>
      {restricted ? (
        <Button color="primary" type="link" icon={<EditOutlined />} disabled />
      ) : (
        <Tooltip
          title={
            disabled
              ? disabledReason ?? translate("popover.extend_expiry_date_fail")
              : firstUse
                ? translate("popover.set_expiry_manually")
                : translate("popover.extend_expiry_date")
          }
        >
          <Button color="primary" type="link" icon={<EditOutlined />} onClick={() => setIsModalOpen(true)} disabled={disabled} />
        </Tooltip>
      )}
      <Modal
        title={translate("title.update_expiry_date")}
        open={isModalOpen}
        onOk={onSubmit}
        onCancel={() => setIsModalOpen(false)}
        okText={translate("button.confirm")}
        afterClose={() => form.resetFields()}
      >
        <Form form={form} layout="vertical" name="updateExpiryDate">
          <Form.Item
            name="expiry_date"
            label={translate("label.expiry_date")}
            rules={[{ required: true, message: `${translate("form.error.select")} ${translate("label.expiry_date")}` }]}
            extra={
              lastClassDay && lastClassDay.isAfter(now().startOf("day"))
                ? translateWithVars("form.hint.expiry_after_last_class", {
                    date: formatDate(lastClassDay.toDate()),
                  })
                : undefined
            }
          >
            <DatePicker
              style={{ width: "100%" }}
              placeholder={`${translate("form.placeholders.select")} ${translate("label.expiry_date")}`}
              format={DEFAULT_DATE_FORMAT}
              disabledDate={(current) => current && current.isBefore(earliestDay)}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default UpdateCustomerPackageModal;
